import {
  LevelActionAddBankrupcy,
  LevelActionDestroySection,
  LevelActionProbabilityAction,
  LevelActionSubstractSectionScore,
  LevelActionWeightedActions,
} from "./ActionTriggers";
import { ActionTrigger, BossLevel, LevelAction } from "./TimelineTypes";
import { Songs } from "./Types";

/**
 * Helper function to create a boss with the given properties
 */
function createBoss(
  difficultyLevel: number,
  name: string,
  description: string,
  requiredScore: number,
  actions: LevelAction[],
  soundtrack?: string,
): BossLevel {
  const boss = new BossLevel();
  boss.name = name;
  boss.description = description;
  boss.requiredScore = requiredScore;
  boss.difficultyLevel = difficultyLevel;
  boss.actions = actions;
  if (soundtrack) {
    boss.soundtrack = soundtrack;
  }
  return boss;
}

export interface Boss {
  name: string;
  description: string;
  requiredScore: number;
  soundtrack: Songs;
  actions: () => LevelAction[];
}

/**
 * Boss pool manager - simple arrays of bosses that get consumed (one-time use)
 */
export class BossPool {
  private static instance: BossPool;
  private bossPoolsByDifficulty: { [difficultyLevel: number]: BossLevel[] } =
    {};

  private constructor() {
    this.resetPools();
  }

  public static getInstance(): BossPool {
    if (!BossPool.instance) {
      BossPool.instance = new BossPool();
    }
    return BossPool.instance;
  }

  /**
   * Reset/initialize boss pools with fresh bosses
   */
  public resetPools(): void {
    this.bossPoolsByDifficulty = {
      1: [],
      2: [],
      3: [],
      4: [],
      5: [],
    };

    this.createBosses();
  }

  private createBosses(): void {
    for (const [_bossType, bosses] of Object.entries(BossData)) {
      for (const [index, boss] of bosses.entries()) {
        const newBoss = createBoss(
          boss.requiredScore,
          boss.name,
          boss.description,
          boss.requiredScore,
          boss.actions(),
        );

        this.bossPoolsByDifficulty[index + 1].push(newBoss);
      }
    }
  }

  /**
   * Get a random boss from the specified difficulty level and remove it from the pool
   * @param difficultyLevel The difficulty level (1-5)
   * @returns A BossLevel instance or null if no bosses available
   */
  public getRandomBoss(difficultyLevel: number): BossLevel | null {
    const pool = this.bossPoolsByDifficulty[difficultyLevel];

    if (!pool || pool.length === 0) {
      console.warn(
        `No bosses available for difficulty level ${difficultyLevel}`,
      );
      return null;
    }

    // Get random boss and remove it from pool (one-time use)
    const randomIndex = Math.floor(Math.random() * pool.length);
    const selectedBoss = pool.splice(randomIndex, 1)[0];

    return selectedBoss;
  }
}

const enum Bosses {
  BANK_THEME = "bank_theme",
  DEMOLITION_THEME = "demolition_theme",
  /*
  CENSORSHIP_THEME,
  VIRUS_THEME,
  GLITCH_THEME,
  BUREAUCRACY_THEME,
  SPEED_THEME,
  CENSORSHIP_THEME
  */
}

export const BossData: { [key in Bosses]: Boss[] } = {
  [Bosses.BANK_THEME]: [
    // bank theme, lowers scores , applies bankruptcies
    // Updated scores to match optimal difficulty curve: 350, 800, 1600, 2800, 3500
    {
      // level 1 boss (level 5 overall) - First major challenge
      name: "The Overdraft",
      description: "",
      requiredScore: 350,
      soundtrack: Songs.BOSS_THEME_1,
      actions: () => [
        new LevelActionSubstractSectionScore(ActionTrigger.POST_RESULT, 10),
      ],
    },
    {
      // level 2 boss (level 8 overall) - Mid-game difficulty spike
      name: "The Repossession",
      description: "",
      requiredScore: 800,
      soundtrack: Songs.BOSS_THEME_2,
      actions: () => [
        new LevelActionWeightedActions(ActionTrigger.POST_RESULT, [
          {
            weight: 9,
            action: new LevelActionSubstractSectionScore(
              ActionTrigger.POST_RESULT,
              20,
            ),
          },
          {
            weight: 1,
            action: new LevelActionAddBankrupcy(ActionTrigger.POST_RESULT, 1),
          },
        ]),
      ],
    },
    {
      // level 3 boss (level 11 overall) - Late game challenge
      name: "The Foreclosure",
      description: "",
      requiredScore: 1600,
      soundtrack: Songs.BOSS_THEME_3,
      actions: () => [
        new LevelActionWeightedActions(ActionTrigger.POST_RESULT, [
          {
            weight: 8,
            action: new LevelActionSubstractSectionScore(
              ActionTrigger.POST_RESULT,
              40,
            ),
          },
          {
            weight: 2,
            action: new LevelActionAddBankrupcy(ActionTrigger.POST_RESULT, 1),
          },
        ]),
      ],
    },
    {
      // level 4 boss (level 14 overall) - Penultimate challenge
      name: "The Insolvency",
      description: "",
      requiredScore: 2800,
      soundtrack: Songs.BOSS_THEME_4,
      actions: () => [
        new LevelActionWeightedActions(ActionTrigger.POST_RESULT, [
          {
            weight: 8,
            action: new LevelActionSubstractSectionScore(
              ActionTrigger.POST_RESULT,
              60,
            ),
          },
          {
            weight: 2,
            action: new LevelActionAddBankrupcy(ActionTrigger.POST_RESULT, 2),
          },
        ]),
      ],
    },
    {
      // level 5 boss (level 15 overall) - Final boss
      name: "The Liquidation",
      description: "",
      requiredScore: 3500,
      soundtrack: Songs.BOSS_THEME_5,
      actions: () => [
        new LevelActionWeightedActions(ActionTrigger.POST_RESULT, [
          {
            weight: 6,
            action: new LevelActionSubstractSectionScore(
              ActionTrigger.POST_RESULT,
              100,
            ),
          },
          {
            weight: 4,
            action: new LevelActionAddBankrupcy(ActionTrigger.POST_RESULT, 2),
          },
        ]),
      ],
    },
  ],
  [Bosses.DEMOLITION_THEME]: [
    // demolition theme, destroys and mines tiles
    // Updated scores to match optimal difficulty curve: 350, 800, 1600, 2800, 3500
    {
      // level 1 boss (level 5 overall) - First major challenge
      name: "The Hammer",
      description: "",
      requiredScore: 350,
      soundtrack: Songs.BOSS_THEME_1,
      actions: () => [
        new LevelActionProbabilityAction(
          ActionTrigger.POST_RESULT,
          new LevelActionDestroySection(ActionTrigger.POST_RESULT, true),
          0.05,
        ),
      ],
    },
    {
      // level 2 boss (level 8 overall) - Mid-game difficulty spike
      name: "The Sledgehammer",
      description: "",
      requiredScore: 800,
      soundtrack: Songs.BOSS_THEME_2,
      actions: () => [
        new LevelActionProbabilityAction(
          ActionTrigger.POST_RESULT,
          new LevelActionDestroySection(ActionTrigger.POST_RESULT, true),
          0.1,
        ),
      ],
    },
    {
      // level 3 boss (level 11 overall) - Late game challenge
      name: "The Jackhammer",
      description: "",
      requiredScore: 1600,
      soundtrack: Songs.BOSS_THEME_3,
      actions: () => [
        new LevelActionProbabilityAction(
          ActionTrigger.POST_RESULT,
          new LevelActionDestroySection(ActionTrigger.POST_RESULT, true),
          0.2,
        ),
      ],
    },
    {
      // level 4 boss (level 14 overall) - Penultimate challenge
      name: "The Wrecking Ball",
      description: "",
      requiredScore: 2800,
      soundtrack: Songs.BOSS_THEME_4,
      actions: () => [
        new LevelActionProbabilityAction(
          ActionTrigger.POST_RESULT,
          new LevelActionDestroySection(ActionTrigger.POST_RESULT),
          0.3,
        ),
      ],
    },
    {
      // level 5 boss (level 15 overall) - Final boss
      name: "The Implosion",
      description: "",
      requiredScore: 3500,
      soundtrack: Songs.BOSS_THEME_5,
      actions: () => [
        new LevelActionProbabilityAction(
          ActionTrigger.POST_RESULT,
          new LevelActionDestroySection(ActionTrigger.POST_RESULT),
          0.5,
        ),
      ],
    },
  ],
};

/*
 1: [
        createBoss(1, "The Overdraft", "", 500, "boss_theme_1"), // bank theme, lowers scores , applies banktrupcies
        createBoss(1, "The Hammer", "", 450, "boss_theme_1"), // demolition theme, destroys and mines tiles
        createBoss(1, "The Breakout", "", 450, "boss_theme_1"), // virus theme, poisons your tiles with slow but steadily increasing effects
        createBoss(1, "The Ping", "", 450, "boss_theme_1"), // glitch theme, randomly changes tile properties, shuffles effects and positions, chaos
        createBoss(1, "The Form", "", 450, "boss_theme_1"), // bureaucracy theme, locks tiles, slowly downgrades tiles
        createBoss(1, "The Pace", "", 450, "boss_theme_1"), // speed theme, lowers max spins and tries to place speed traps that go towards bad tiles
        createBoss(1, "The Redaction", "", 450, "boss_theme_1"), // censorship theme, hides tiles, places hidden effects that reveal on landing ?
      ],
      2: [
        createBoss(2, "The Repossession", "", 1000, "boss_theme_2"),
        createBoss(2, "The Sledgehammer", "", 950, "boss_theme_2"),
        createBoss(2, "The Virus", "", 950, "boss_theme_2"),
        createBoss(2, "The Lag", "", 950, "boss_theme_2"),
        createBoss(2, "The Stamp", "", 950, "boss_theme_2"),
        createBoss(2, "The Rush", "", 950, "boss_theme_2"),
        createBoss(2, "The Classified", "", 950, "boss_theme_2"),
      ],
      3: [
        createBoss(3, "The Foreclosure", "", 1500, "boss_theme_3"),
        createBoss(3, "The Jackhammer", "", 1450, "boss_theme_3"),
        createBoss(3, "The Contagion", "", 1450, "boss_theme_3"),
        createBoss(3, "The Glitch", "", 1450, "boss_theme_3"),
        createBoss(3, "The Red Tape", "", 1450, "boss_theme_3"),
        createBoss(3, "The Drift", "", 1450, "boss_theme_3"),
        createBoss(3, "The Black Bars", "", 1450, "boss_theme_3"),
      ],
      4: [
        createBoss(4, "The Insolvency", "", 2000, "boss_theme_4"),
        createBoss(4, "The Wrecking Ball", "", 1950, "boss_theme_4"),
        createBoss(4, "The Epidemic", "", 1950, "boss_theme_4"),
        createBoss(4, "The Memory Leak", "", 1950, "boss_theme_4"),
        createBoss(4, "The Audit", "", 1950, "boss_theme_4"),
        createBoss(4, "The Overdrive", "", 1950, "boss_theme_4"),
        createBoss(4, "The Gag Order", "", 1950, "boss_theme_4"),
      ],
      5: [
        createBoss(5, "The Liquidation", "", 2500, "final_boss_theme"),
        createBoss(5, "The Implosion", "", 2450, "final_boss_theme"),
        createBoss(5, "The Pandemic", "", 2450, "final_boss_theme"),
        createBoss(5, "The Kernel Panic", "", 2450, "final_boss_theme"),
        createBoss(5, "The Sanction", "", 2450, "final_boss_theme"),
        createBoss(5, "The Redline", "", 2450, "final_boss_theme"),
        createBoss(5, "The Censor", "", 2450, "final_boss_theme"),
      ],
      */
